import {
  createContext,
  ReactNode,
  useContext,
  useLayoutEffect,
  useState
} from 'react';

const PortalContainerContext = createContext<HTMLElement | null>(null);

/**
 * Where overlays (dialogs, menus, tooltips, drag previews) portal to: the
 * nearest `PortalContainerScope`'s element, else `#tailwind-portals`.
 */
export function usePortalContainer(): HTMLElement {
  const scoped = useContext(PortalContainerContext);
  return scoped ?? document.getElementById('tailwind-portals') ?? document.body;
}

/**
 * Overlays opened from `children` portal into an element of their own that
 * carries `className` and `variables`, so they look like the region that
 * opened them (a theme's `dark`, a page's own colour variables).
 */
export function PortalContainerScope({
  className,
  variables,
  children
}: {
  className?: string;
  /** CSS custom properties set on the element, `--name` keys. */
  variables?: Record<string, string>;
  children: ReactNode;
}) {
  const parent = usePortalContainer();
  const [container] = useState(() => document.createElement('div'));
  useLayoutEffect(() => {
    parent.appendChild(container);
    return () => container.remove();
  }, [parent, container]);
  useLayoutEffect(() => {
    container.className = className ?? '';
  }, [container, className]);
  // by value: a host's object is new on every render
  const variablesKey = JSON.stringify(variables ?? {});
  useLayoutEffect(() => {
    const set: Record<string, string> = JSON.parse(variablesKey);
    for (const [name, value] of Object.entries(set)) {
      container.style.setProperty(name, value);
    }
    return () => {
      for (const name of Object.keys(set)) container.style.removeProperty(name);
    };
  }, [container, variablesKey]);
  return (
    <PortalContainerContext.Provider value={container}>
      {children}
    </PortalContainerContext.Provider>
  );
}
